import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, X, Upload, Star, Award, Sparkles, Tag, Bookmark } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { InlineSpinner } from '../components/InlineSpinner';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import styles from './PaintKitsPage.module.css';

interface KitImage {
  url: string;
  isDefault: boolean;
  _id?: string;
}

interface NamedRef {
  _id: string;
  name: string;
}

interface PaintKit {
  _id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  images: KitImage[];
  coverImage: string;
  size: string;
  difficulty: string;
  inStock: boolean;
  included: string[];
  paintingTime: string;
  framed: boolean;
  howToUse: string;
  bestSeller: boolean;
  latestCollection: boolean;
  productCollection: NamedRef | null;
  categories: NamedRef[];
  createdAt: string;
}

const initialFormState = {
  name: '',
  description: '',
  price: 0,
  size: '',
  difficulty: 'beginner',
  inStock: true,
  paintingTime: '',
  framed: false,
  howToUse: '',
  bestSeller: false,
  latestCollection: false,
  collection: '',
  categoryIds: [] as string[],
};

function getImageSrc(url: string) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url}`;
}

function getCoverImageSrc(kit: PaintKit) {
  if (kit.images && kit.images.length > 0) {
    const defaultImg = kit.images.find((img) => img.isDefault) || kit.images[0];
    return getImageSrc(defaultImg.url);
  }
  return kit.imageUrl ? getImageSrc(kit.imageUrl) : '';
}

export function PaintKitsPage() {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [paintKits, setPaintKits] = useState<PaintKit[]>([]);
  const [collections, setCollections] = useState<NamedRef[]>([]);
  const [categories, setCategories] = useState<NamedRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingKit, setEditingKit] = useState<PaintKit | null>(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Image state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<KitImage[]>([]);
  const [defaultImageIndex, setDefaultImageIndex] = useState<number>(0);

  const fetchPaintKits = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/painting-kits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setPaintKits(data.kits || []);
    } catch (error) {
      console.error('Error fetching paint kits:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setCollections(data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchPaintKits();
    fetchCollections();
    fetchCategories();
  }, [token]);

  const openCreateModal = () => {
    setEditingKit(null);
    setFormData(initialFormState);
    setSelectedFiles([]);
    setExistingImages([]);
    setDefaultImageIndex(0);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (kit: PaintKit) => {
    setEditingKit(kit);
    setFormData({
      name: kit.name,
      description: kit.description,
      price: kit.price,
      size: kit.size,
      difficulty: kit.difficulty,
      inStock: kit.inStock,
      paintingTime: kit.paintingTime || '',
      framed: kit.framed || false,
      howToUse: kit.howToUse || '',
      bestSeller: kit.bestSeller || false,
      latestCollection: kit.latestCollection || false,
      collection: kit.productCollection?._id || '',
      categoryIds: (kit.categories || []).map((c) => c._id),
    });

    const images = kit.images && kit.images.length > 0
      ? kit.images
      : kit.imageUrl
        ? [{ url: kit.imageUrl, isDefault: true }]
        : [];
    setExistingImages(images);
    setSelectedFiles([]);
    const defaultIdx = images.findIndex((img) => img.isDefault);
    setDefaultImageIndex(defaultIdx >= 0 ? defaultIdx : 0);
    setError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingKit(null);
    setFormData(initialFormState);
    setSelectedFiles([]);
    setExistingImages([]);
    setDefaultImageIndex(0);
    setError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));
  };

  const toggleCategory = (categoryId: string) => {
    setFormData((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId],
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
      if (existingImages.length === 0 && selectedFiles.length === 0) {
        setDefaultImageIndex(0);
      }
    }
    e.target.value = '';
  };

  const removeExistingImage = (idx: number) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== idx));
    if (defaultImageIndex === idx) {
      setDefaultImageIndex(0);
    } else if (defaultImageIndex > idx) {
      setDefaultImageIndex((prev) => prev - 1);
    }
  };

  const removeNewFile = (idx: number) => {
    const combinedIdx = existingImages.length + idx;
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
    if (defaultImageIndex === combinedIdx) {
      setDefaultImageIndex(0);
    } else if (defaultImageIndex > combinedIdx) {
      setDefaultImageIndex((prev) => prev - 1);
    }
  };

  const totalImages = existingImages.length + selectedFiles.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (totalImages === 0) {
      setError('Please upload at least one image');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const url = editingKit
        ? `${API_BASE_URL}/api/painting-kits/${editingKit._id}`
        : `${API_BASE_URL}/api/painting-kits`;
      const method = editingKit ? 'PUT' : 'POST';

      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('description', formData.description);
      fd.append('price', String(formData.price));
      fd.append('size', formData.size);
      fd.append('difficulty', formData.difficulty);
      fd.append('inStock', String(formData.inStock));
      fd.append('paintingTime', formData.paintingTime);
      fd.append('framed', String(formData.framed));
      fd.append('howToUse', formData.howToUse);
      fd.append('bestSeller', String(formData.bestSeller));
      fd.append('latestCollection', String(formData.latestCollection));
      fd.append('collection', formData.collection);
      fd.append('categories', JSON.stringify(formData.categoryIds));

      if (editingKit && existingImages.length > 0) {
        fd.append('existingImages', JSON.stringify(existingImages));
      }

      selectedFiles.forEach((file) => {
        fd.append('images', file);
      });

      fd.append('defaultImageIndex', String(defaultImageIndex));

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save paint kit');
      }

      closeModal();
      fetchPaintKits();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      message: 'Are you sure you want to delete this paint kit? This cannot be undone.',
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setDeletingId(id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/painting-kits/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to delete paint kit');
      }

      await fetchPaintKits();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Paint Kits</h1>
          <p>Manage your paint-by-numbers products</p>
        </div>
        <button className={styles.addBtn} onClick={openCreateModal}>
          <Plus size={20} />
          Add Paint Kit
        </button>
      </header>

      {loading ? (
        <div className={styles.loading}>Loading paint kits...</div>
      ) : paintKits.length === 0 ? (
        <div className={styles.empty}>
          <p>No paint kits yet. Add your first product!</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {paintKits.map((kit) => (
            <div key={kit._id} className={styles.card}>
              <div className={styles.cardImage}>
                <img src={getCoverImageSrc(kit)} alt={kit.name} />
                {!kit.inStock && <div className={styles.outOfStock}>Out of Stock</div>}
                {kit.images && kit.images.length > 1 && (
                  <div className={styles.imageCount}>{kit.images.length} images</div>
                )}
                <div className={styles.badgeStack}>
                  {kit.bestSeller && (
                    <span className={styles.bestSellerBadge}>
                      <Award size={12} /> Best Seller
                    </span>
                  )}
                  {kit.latestCollection && (
                    <span className={styles.newBadge}>
                      <Sparkles size={12} /> New
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardHeader}>
                  <h3>{kit.name}</h3>
                  <span className={styles.price}>EGP {kit.price}</span>
                </div>
                <p className={styles.description}>{kit.description}</p>
                <div className={styles.tags}>
                  <span className={styles.tag}>{kit.size}</span>
                  <span className={styles.tag}>{kit.difficulty}</span>
                  {kit.paintingTime && <span className={styles.tag}>{kit.paintingTime}</span>}
                  {kit.framed && <span className={styles.tag}>Framed</span>}
                </div>
                {(kit.productCollection || (kit.categories && kit.categories.length > 0)) && (
                  <div className={styles.tags}>
                    {kit.productCollection && (
                      <span className={styles.collectionTag}>
                        <Tag size={11} /> {kit.productCollection.name}
                      </span>
                    )}
                    {(kit.categories || []).map((cat) => (
                      <span key={cat._id} className={styles.categoryTag}>
                        <Bookmark size={11} /> {cat.name}
                      </span>
                    ))}
                  </div>
                )}
                <div className={styles.cardFooter}>
                  <span className={`${styles.stockStatus} ${kit.inStock ? styles.inStock : styles.noStock}`}>
                    {kit.inStock ? 'In Stock' : 'Out of Stock'}
                  </span>
                  <div className={styles.actions}>
                    <button
                      className={styles.editBtn}
                      onClick={() => openEditModal(kit)}
                      disabled={deletingId === kit._id}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(kit._id)}
                      disabled={deletingId === kit._id}
                    >
                      {deletingId === kit._id ? <InlineSpinner size={16} /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={closeModal}>
              <X size={20} />
            </button>
            <h2>{editingKit ? 'Edit Paint Kit' : 'Add Paint Kit'}</h2>

            {error && <div className={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Price (EGP)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min={0}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Size</label>
                  <input
                    type="text"
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    placeholder="e.g., 20x30 cm, 40x50 cm"
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Difficulty</label>
                  <select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleChange}
                    required
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Stock Status</label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      name="inStock"
                      checked={formData.inStock}
                      onChange={handleChange}
                    />
                    In Stock
                  </label>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Painting Time</label>
                  <input
                    type="text"
                    name="paintingTime"
                    value={formData.paintingTime}
                    onChange={handleChange}
                    placeholder="e.g., 4-6 hours"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Framed</label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      name="framed"
                      checked={formData.framed}
                      onChange={handleChange}
                    />
                    Framed
                  </label>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>How to Use</label>
                <textarea
                  name="howToUse"
                  value={formData.howToUse}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Instructions for using this kit"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Best Seller</label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      name="bestSeller"
                      checked={formData.bestSeller}
                      onChange={handleChange}
                    />
                    Show "Best Seller" badge
                  </label>
                </div>
                <div className={styles.formGroup}>
                  <label>New Launch</label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      name="latestCollection"
                      checked={formData.latestCollection}
                      onChange={handleChange}
                    />
                    Show "New" badge
                  </label>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Collection</label>
                <select
                  name="collection"
                  value={formData.collection}
                  onChange={handleChange}
                >
                  <option value="">No collection</option>
                  {collections.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Categories</label>
                {categories.length === 0 ? (
                  <p className={styles.uploadHint}>
                    No categories yet — add some on the Categories page.
                  </p>
                ) : (
                  <div className={styles.categoryChecks}>
                    {categories.map((cat) => (
                      <label key={cat._id} className={styles.checkbox}>
                        <input
                          type="checkbox"
                          checked={formData.categoryIds.includes(cat._id)}
                          onChange={() => toggleCategory(cat._id)}
                        />
                        {cat.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Image Upload Section */}
              <div className={styles.formGroup}>
                <label>Images {totalImages === 0 && <span style={{ color: 'var(--danger)' }}>*</span>}</label>
                <label className={styles.uploadArea}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <Upload size={20} />
                  <span>Click to add images</span>
                  <span className={styles.uploadHint}>JPEG, PNG, WebP (max 10MB each)</span>
                </label>

                {totalImages > 0 && (
                  <div className={styles.imagePreviews}>
                    {existingImages.map((img, idx) => (
                      <div
                        key={img.url}
                        className={`${styles.imagePreview} ${defaultImageIndex === idx ? styles.imagePreviewDefault : ''}`}
                      >
                        <img src={getImageSrc(img.url)} alt={`Image ${idx + 1}`} />
                        <div className={styles.imagePreviewActions}>
                          <button
                            type="button"
                            className={`${styles.coverBtn} ${defaultImageIndex === idx ? styles.coverBtnActive : ''}`}
                            onClick={() => setDefaultImageIndex(idx)}
                            title="Set as cover"
                          >
                            <Star size={14} />
                          </button>
                          <button
                            type="button"
                            className={styles.removeImgBtn}
                            onClick={() => removeExistingImage(idx)}
                            title="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        {defaultImageIndex === idx && (
                          <span className={styles.coverLabel}>Cover</span>
                        )}
                      </div>
                    ))}

                    {selectedFiles.map((file, idx) => {
                      const combinedIdx = existingImages.length + idx;
                      return (
                        <div
                          key={`new-${idx}`}
                          className={`${styles.imagePreview} ${defaultImageIndex === combinedIdx ? styles.imagePreviewDefault : ''}`}
                        >
                          <img src={URL.createObjectURL(file)} alt={file.name} />
                          <div className={styles.imagePreviewActions}>
                            <button
                              type="button"
                              className={`${styles.coverBtn} ${defaultImageIndex === combinedIdx ? styles.coverBtnActive : ''}`}
                              onClick={() => setDefaultImageIndex(combinedIdx)}
                              title="Set as cover"
                            >
                              <Star size={14} />
                            </button>
                            <button
                              type="button"
                              className={styles.removeImgBtn}
                              onClick={() => removeNewFile(idx)}
                              title="Remove"
                            >
                              <X size={14} />
                            </button>
                          </div>
                          {defaultImageIndex === combinedIdx && (
                            <span className={styles.coverLabel}>Cover</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <button type="submit" className={styles.submitBtn} disabled={submitting}>
                {submitting ? 'Saving...' : editingKit ? 'Update Paint Kit' : 'Add Paint Kit'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
